
import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";

interface DebugModalProps {
  isOpen: boolean;
  onClose: () => void;
  error: any;
}

const DebugModal = ({ isOpen, onClose, error }: DebugModalProps) => {
  if (!error) return null;

  const getErrorColor = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'profile error':
        return 'bg-yellow-500';
      case 'role mismatch':
        return 'bg-red-500';
      case 'unexpected error':
        return 'bg-purple-500';
      default:
        return 'bg-gray-500';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <DialogTitle>Debug Information</DialogTitle>
            {error.type && (
              <Badge className={`${getErrorColor(error.type)}`}>
                {error.type}
              </Badge>
            )}
          </div>
          {error.userId && (
            <DialogDescription>
              User ID: {error.userId}
            </DialogDescription>
          )}
        </DialogHeader>
        
        <ScrollArea className="max-h-[60vh]">
          <div className="space-y-4">
            {/* Role Information */}
            {(error.currentRole || error.requiredRole) && (
              <div className="rounded-md bg-slate-100 dark:bg-slate-800 p-4">
                <h3 className="font-medium mb-2">Role Information</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>Current Role: <Badge variant="outline">{error.currentRole || 'None'}</Badge></div>
                  <div>Required Role: <Badge variant="outline">{error.requiredRole || 'None'}</Badge></div>
                </div>
              </div>
            )}

            {/* Location Information */}
            {error.location && (
              <div className="rounded-md bg-slate-100 dark:bg-slate-800 p-4">
                <h3 className="font-medium mb-2">Location</h3>
                <code className="text-sm">{error.location}</code>
              </div>
            )}

            {/* Error Details */}
            {error.error && (
              <div className="rounded-md bg-slate-100 dark:bg-slate-800 p-4">
                <h3 className="font-medium mb-2">Error Details</h3>
                <div className="space-y-2 text-sm">
                  {error.error.code && (
                    <div>Code: <code>{error.error.code}</code></div>
                  )}
                  {error.error.message && (
                    <div>Message: <code>{error.error.message}</code></div>
                  )}
                  {error.error.details && (
                    <div>Details: <code>{error.error.details}</code></div>
                  )}
                </div>
              </div>
            )}

            {/* Full Debug Object */}
            <div className="rounded-md bg-slate-100 dark:bg-slate-800 p-4">
              <h3 className="font-medium mb-2">Full Debug Object</h3>
              <pre className="whitespace-pre-wrap text-sm overflow-auto">
                {JSON.stringify(error, null, 2)}
              </pre>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default DebugModal;
